import {
  Color,
  Group,
  Mesh,
  Object3D,
  ShaderMaterial,
  SphereGeometry,
} from "three"
import { AbstractModel } from "./AbstractModel"
import { getSeabedSandTexture } from "./Seabed"

const SPHERE_RADIUS = 0.52
const SPHERE_WIDTH_SEGMENTS = 34
const SPHERE_HEIGHT_SEGMENTS = 38

type ShaderUniform<T> = { value: T }

type WasteWaterSphereVisual = {
  mesh: Mesh
  uHealth: ShaderUniform<number>
  uTime: ShaderUniform<number>
}

const simplexNoise4D = /* glsl */ `
vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

float mod289(float x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
  return mod289(((x * 34.0) + 10.0) * x);
}

float permute(float x) {
  return mod289(((x * 34.0) + 10.0) * x);
}

vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

float taylorInvSqrt(float r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec4 grad4(float j, vec4 ip) {
  const vec4 ones = vec4(1.0, 1.0, 1.0, -1.0);
  vec4 p;
  vec4 s;

  p.xyz = floor(fract(vec3(j) * ip.xyz) * 7.0) * ip.z - 1.0;
  p.w = 1.5 - dot(abs(p.xyz), ones.xyz);
  s = vec4(lessThan(p, vec4(0.0)));
  p.xyz = p.xyz + (s.xyz * 2.0 - 1.0) * s.www;
  return p;
}

#define F4 0.309016994374947451

float snoise4(vec4 v) {
  const vec4 C = vec4(
    0.138196601125011,
    0.276393202250021,
    0.414589803375032,
    -0.447213595499958
  );

  vec4 i = floor(v + dot(v, vec4(F4)));
  vec4 x0 = v - i + dot(i, C.xxxx);

  vec4 i0;
  vec3 isX = step(x0.yzw, x0.xxx);
  vec3 isYZ = step(x0.zww, x0.yyz);
  i0.x = isX.x + isX.y + isX.z;
  i0.yzw = 1.0 - isX;
  i0.y += isYZ.x + isYZ.y;
  i0.zw += 1.0 - isYZ.xy;
  i0.z += isYZ.z;
  i0.w += 1.0 - isYZ.z;

  vec4 i3 = clamp(i0, 0.0, 1.0);
  vec4 i2 = clamp(i0 - 1.0, 0.0, 1.0);
  vec4 i1 = clamp(i0 - 2.0, 0.0, 1.0);

  vec4 x1 = x0 - i1 + C.xxxx;
  vec4 x2 = x0 - i2 + C.yyyy;
  vec4 x3 = x0 - i3 + C.zzzz;
  vec4 x4 = x0 + C.wwww;

  i = mod289(i);
  float j0 = permute(permute(permute(permute(i.w) + i.z) + i.y) + i.x);
  vec4 j1 = permute(permute(permute(permute(
    i.w + vec4(i1.w, i2.w, i3.w, 1.0)) +
    i.z + vec4(i1.z, i2.z, i3.z, 1.0)) +
    i.y + vec4(i1.y, i2.y, i3.y, 1.0)) +
    i.x + vec4(i1.x, i2.x, i3.x, 1.0));

  vec4 ip = vec4(1.0 / 294.0, 1.0 / 49.0, 1.0 / 7.0, 0.0);

  vec4 p0 = grad4(j0, ip);
  vec4 p1 = grad4(j1.x, ip);
  vec4 p2 = grad4(j1.y, ip);
  vec4 p3 = grad4(j1.z, ip);
  vec4 p4 = grad4(j1.w, ip);

  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  p4 *= taylorInvSqrt(dot(p4, p4));

  vec3 m0 = max(0.57 - vec3(dot(x0, x0), dot(x1, x1), dot(x2, x2)), 0.0);
  vec2 m1 = max(0.57 - vec2(dot(x3, x3), dot(x4, x4)), 0.0);
  m0 = m0 * m0;
  m1 = m1 * m1;
  return 60.1 * (
    dot(m0 * m0, vec3(dot(p0, x0), dot(p1, x1), dot(p2, x2))) +
    dot(m1 * m1, vec2(dot(p3, x3), dot(p4, x4)))
  );
}

float fbm4(vec4 p) {
  float value = 0.0;
  float amplitude = 0.55;
  for (int i = 0; i < 3; i++) {
    value += amplitude * snoise4(p);
    p.xyz *= 2.03;
    p.w *= 1.17;
    amplitude *= 0.5;
  }
  return value;
}

float morphHeight(vec3 dir, float time, float noiseScale, float timeScale) {
  vec4 samplePos = vec4(dir * noiseScale, time * timeScale);
  return fbm4(samplePos);
}
`

const vertexShader = /* glsl */ `
${simplexNoise4D}

uniform float uTime;
uniform float uRadius;
uniform float uMorphAmp;
uniform float uNoiseScale;
uniform float uTimeScale;
uniform float uNormalEpsilon;

varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec3 vWorldTangent;
varying vec3 vWorldBitangent;
varying vec3 vRadial;
varying float vCrease;
varying float vTint;
varying vec2 vUv;

void main() {
  vUv = uv;
  vec3 seedDir = normalize(position);
  float h0 = morphHeight(seedDir, uTime, uNoiseScale, uTimeScale);
  vec3 ref = abs(seedDir.y) < 0.92 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
  vec3 tangent = normalize(cross(ref, seedDir));
  vec3 bitangent = cross(seedDir, tangent);

  float eps = uNormalEpsilon;
  float hT = morphHeight(normalize(seedDir + tangent * eps), uTime, uNoiseScale, uTimeScale);
  float hB = morphHeight(normalize(seedDir + bitangent * eps), uTime, uNoiseScale, uTimeScale);

  float dHdu = (hT - h0) / eps;
  float dHdv = (hB - h0) / eps;
  float shell = uRadius + h0 * uMorphAmp;

  vec3 displaced = seedDir * shell;
  vec3 dPdu = tangent * shell + seedDir * (dHdu * uMorphAmp);
  vec3 dPdv = bitangent * shell + seedDir * (dHdv * uMorphAmp);
  vec3 objectNormal = normalize(cross(dPdu, dPdv));

  vec4 worldPos = modelMatrix * vec4(displaced, 1.0);
  vWorldPos = worldPos.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
  vWorldTangent = normalize(mat3(modelMatrix) * tangent);
  vWorldBitangent = normalize(mat3(modelMatrix) * bitangent);
  vRadial = normalize(mat3(modelMatrix) * seedDir);
  vCrease = clamp(length(objectNormal - seedDir) * 3.2, 0.0, 1.0);
  vTint = clamp(seedDir.y * 0.5 + 0.5 + snoise4(vec4(seedDir * 2.4, uTime * 0.11)) * 0.18, 0.0, 1.0);

  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`

const fragmentShader = /* glsl */ `

uniform float uTime;
uniform float uHealth;
uniform vec3 uDarkColor;
uniform vec3 uMidColor;
uniform float uCreaseStrength;
uniform float uGrainStrength;
uniform sampler2D uMap;

varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec3 vWorldTangent;
varying vec3 vWorldBitangent;
varying vec3 vRadial;
varying float vCrease;
varying float vTint;
varying vec2 vUv;

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 T = normalize(vWorldTangent);
  vec3 B = normalize(vWorldBitangent);
  vec3 V = normalize(cameraPosition - vWorldPos);

  float grain = texture2D(uMap, vUv).r;

  vec3 lightDir = normalize(vec3(0.35, 0.92, 0.28));
  float ndl = max(dot(N, lightDir), 0.0);
  float wrap = max(dot(N, lightDir) * 0.5 + 0.5, 0.0);

  float radialFold = pow(1.0 - abs(dot(N, normalize(vRadial))), 2.2);
  float crease = (pow(vCrease, 1.35) + radialFold * 0.28) * uCreaseStrength;
  float foldRim = pow(1.0 - max(dot(N, V), 0.0), 2.4) * crease;
  vec3 H = normalize(lightDir + V);
  float tangentSpec = pow(max(dot(T, H), 0.0), 18.0) * crease * 0.85;
  float bitangentSpec = pow(max(dot(B, H), 0.0), 26.0) * crease * 0.45;

  vec3 baseCol = mix(uDarkColor, uMidColor, vTint);
  baseCol *= 0.72 + grain * 0.34;
  baseCol *= 0.55 + wrap * 0.45 + ndl * 0.22;
  baseCol += vec3(0.035, 0.05, 0.03) * foldRim;
  baseCol += vec3(0.06, 0.075, 0.05) * (tangentSpec + bitangentSpec);

  float alpha = (0.52 + crease * 0.48) * pow(uHealth, 0.65);
  gl_FragColor = vec4(baseCol, alpha);
}
`

function syncWasteWaterVisual(visual: WasteWaterSphereVisual, host: Group) {
  const h = host.userData.health as number
  const m = host.userData.maxHealth as number
  const health01 = m < 1 ? 0 : Math.max(0, Math.min(1, h / m))
  visual.uHealth.value = health01
  visual.mesh.visible = health01 > 0
}

/** Сфера с 4D simplex-морфингом, касательным освещением заворотов и зернистой текстурой. */
function createWasteWaterSphere(host: Group): WasteWaterSphereVisual {
  const geometry = new SphereGeometry(
    SPHERE_RADIUS,
    SPHERE_WIDTH_SEGMENTS,
    SPHERE_HEIGHT_SEGMENTS,
  )

  const uHealth: ShaderUniform<number> = { value: 1 }
  const uTime: ShaderUniform<number> = { value: 0 }

  const material = new ShaderMaterial({
    uniforms: {
      uTime,
      uHealth,
      uRadius: { value: SPHERE_RADIUS },
      uMorphAmp: { value: 0.41 },
      uNoiseScale: { value: 0.85 },
      uTimeScale: { value: 0.32 },
      uNormalEpsilon: { value: 0.006 },
      uDarkColor: { value: new Color("#000000") },
      uMidColor: { value: new Color("#868964") },
      uCreaseStrength: { value: 0.85 },
      uGrainStrength: { value: 3.92 },
      uMap: { value: getSeabedSandTexture() },
    },
    vertexShader,
    fragmentShader,
    transparent: true,
  })

  const mesh = new Mesh(geometry, material)
  mesh.frustumCulled = false
  mesh.renderOrder = 999
  mesh.userData.skipGarbagePbr = true
  // mesh.layers.set(1)

  const startSec = performance.now() / 1000
  const visual: WasteWaterSphereVisual = { mesh, uHealth, uTime }

  mesh.onBeforeRender = () => {
    syncWasteWaterVisual(visual, host)
    uTime.value = performance.now() / 1000 - startSec
  }

  return visual
}

export class WasteWater implements AbstractModel {
  constructor(private readonly maxHealth: number = 40) {}

  get key(): string {
    return "waste_water"
  }

  isLoaded = true

  getModel(): Object3D | null {
    const group = new Group()
    const maxH = Math.max(1, Math.floor(this.maxHealth))

    group.userData.type = "box"
    group.userData.wasteWater = true
    group.userData.skipGarbagePbr = true
    group.userData.health = maxH
    group.userData.maxHealth = maxH

    const hitMesh = new Mesh(new SphereGeometry(SPHERE_RADIUS, 20, 20))
    hitMesh.visible = false
    hitMesh.userData.skipGarbagePbr = true
    group.add(hitMesh)

    const sphereVisual = createWasteWaterSphere(group)
    group.userData.syncWasteWaterParticles = () => {
      syncWasteWaterVisual(sphereVisual, group)
    }
    group.add(sphereVisual.mesh)

    return group
  }

  load(onLoad: () => void) {
    onLoad()
  }
}
